import React from 'react';
import { FaBrain, FaChartLine, FaRocket, FaCogs, FaUsers, FaCrosshairs, FaMapMarkerAlt, FaBullhorn, FaInstagram, FaGoogle, FaFacebook } from 'react-icons/fa';
import { SiShopify, SiSquare } from 'react-icons/si';

function HowItWorksPage() {
  const steps = [
    {
      number: 1,
      icon: <FaMapMarkerAlt />,
      title: 'Connect Your Local Business',
      description: 'Link your Google Business Profile, social media accounts, and POS systems. We integrate with Toast, Square, Shopify, and more.'
    },
    {
      number: 2,
      icon: <FaBrain />,
      title: 'AI Analyzes Your Market',
      description: 'Our AI scans your local competition, customer demographics, and market trends to understand your unique position.'
    },
    {
      number: 3,
      icon: <FaCrosshairs />,
      title: 'Get Your Personalized Strategy',
      description: 'Receive a custom digital marketing plan tailored to your business goals, budget, and local market conditions.'
    },
    {
      number: 4,
      icon: <FaBullhorn />,
      title: 'Launch AI-Powered Campaigns',
      description: 'Deploy automated social media posts, targeted local ads, and email campaigns that reach the right customers.'
    },
    {
      number: 5,
      icon: <FaRocket />,
      title: 'Grow Your Local Presence',
      description: 'Watch your online visibility increase, attract more local customers, and track real results in your dashboard.'
    }
  ];

  const aiSolutions = [
    {
      icon: <FaBrain />,
      title: 'AI-Powered Business Intelligence',
      description: 'Smart recommendations powered by machine learning that analyze your sales data, customer patterns, and market trends to identify growth opportunities.',
      features: ['Sales trend analysis', 'Customer behavior insights', 'Predictive forecasting']
    },
    {
      icon: <FaCrosshairs />,
      title: 'Strategic Growth Planning',
      description: 'Data-driven business strategies that help you set achievable goals, track KPIs, and make informed decisions for sustainable growth.',
      features: ['Goal setting & tracking', 'KPI dashboards', 'Growth roadmaps']
    },
    {
      icon: <FaChartLine />,
      title: 'Market & Competitor Analysis',
      description: 'Understand your competitive landscape with real-time monitoring of local competitors, pricing trends, and market positioning.',
      features: ['Competitor monitoring', 'Price intelligence', 'Market share analysis']
    },
    {
      icon: <FaCogs />,
      title: 'Business Process Automation',
      description: 'Automate repetitive marketing tasks with AI-powered workflows for social posting, email campaigns, and customer engagement.',
      features: ['Social media scheduling', 'Email automation', 'Review management']
    },
    {
      icon: <FaUsers />,
      title: 'Customer Behavior Analytics',
      description: 'Deep insights into your customers\' preferences, purchase patterns, and engagement to create personalized marketing experiences.',
      features: ['Customer segmentation', 'Purchase pattern analysis', 'Loyalty tracking']
    },
    {
      icon: <FaRocket />,
      title: 'Revenue Optimization Tools',
      description: 'Maximize profitability with AI-driven pricing suggestions, upsell recommendations, and promotional timing optimization.',
      features: ['Dynamic pricing insights', 'Promotion optimization', 'Revenue forecasting']
    }
  ];

  const integrations = [
    { icon: <FaGoogle />, name: 'Google Business', color: '#4285F4' },
    { icon: <FaInstagram />, name: 'Instagram', color: '#E4405F' },
    { icon: <FaFacebook />, name: 'Facebook', color: '#1877F2' },
    { icon: <SiShopify />, name: 'Shopify', color: '#7AB55C' },
    { icon: <span style={{ fontWeight: 'bold', fontSize: '1.2rem' }}>T</span>, name: 'Toast POS', color: '#FF6600' },
    { icon: <SiSquare />, name: 'Square', color: '#006AFF' }
  ];

  return (
    <div className="page-content">
      <div className="how-it-works-container">
        <div className="how-it-works-header">
          <h1>How It Works</h1>
          <p>AI-powered digital marketing designed specifically for local businesses. Connect, analyze, automate, and grow.</p>
        </div>

        <div className="steps-section">
          <div className="steps-list">
            {steps.map((step) => (
              <div key={step.number} className="step-card">
                <div className="step-number">{step.number}</div>
                <div className="step-icon">{step.icon}</div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="features-section">
          <h2>Personalized AI Solutions for Your Business</h2>
          <p className="section-subtitle">Choose the tools that match your goals. Our AI adapts to your specific needs.</p>
          <div className="features-grid solutions-grid">
            {aiSolutions.map((solution, index) => (
              <div key={index} className="feature-card solution-card">
                <div className="feature-icon">{solution.icon}</div>
                <h3>{solution.title}</h3>
                <p>{solution.description}</p>
                <ul className="solution-features">
                  {solution.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="integrations-section">
          <h2>Connects With Your Favorite Tools</h2>
          <div className="integrations-grid">
            {integrations.map((integration, index) => (
              <div key={index} className="integration-item">
                <div className="integration-icon" style={{ color: integration.color }}>
                  {integration.icon}
                </div>
                <span>{integration.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="cta-section">
          <h2>Ready to Transform Your Local Business?</h2>
          <p>Join the waitlist and be among the first to experience AI-powered local marketing.</p>
        </div>
      </div>
    </div>
  );
}

export default HowItWorksPage;
